"use client";

import { useRef } from "react";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
  type MotionValue,
} from "framer-motion";
import { PhosphorIcon } from "./PhosphorIcon";
import type { IStoryBeat } from "@/lib/sanity/types";

interface IPinnedStoryProps {
  heading: string;
  beats: IStoryBeat[];
}

/** Fraction of each beat's segment spent fading in/out. */
const FADE = 0.2;

/**
 * Scroll runway per beat count (~105vh per beat), desktop only — literal
 * classes so Tailwind can see them. Mobile keeps auto height (stacked layout).
 */
const STORY_HEIGHT_CLASS: Record<number, string> = {
  1: "lg:h-[110vh]",
  2: "lg:h-[210vh]",
  3: "lg:h-[315vh]",
  4: "lg:h-[420vh]",
  5: "lg:h-[525vh]",
  6: "lg:h-[630vh]",
};

/** Accent colours per tone; problem beats alternate warm hues like the design. */
function statColor(beat: IStoryBeat, index: number): string {
  if (beat.tone === "solution") return "var(--color-mint-leaf-400)";
  return index % 2 === 0
    ? "var(--color-burnt-peach-400)"
    : "var(--color-blush-rose-400)";
}

function tagColor(beat: IStoryBeat): string {
  return beat.tone === "solution"
    ? "var(--color-mint-leaf-400)"
    : "rgba(246,239,243,.62)";
}

function BeatPanel({
  beat,
  index,
}: {
  beat: IStoryBeat;
  index: number;
}): React.ReactElement {
  const accent = statColor(beat, index);
  return (
    <div className="w-full max-w-[420px] rounded-[20px] border border-mauve-shadow-50/15 bg-mauve-shadow-50/5 p-6 backdrop-blur-sm sm:p-7">
      {beat.panelLabel && (
        <div className="font-sans text-[11px] font-semibold uppercase leading-none tracking-[.1em] text-mauve-shadow-50/50">
          {beat.panelLabel}
        </div>
      )}
      {beat.stat && (
        <div
          className="mt-4 font-sans text-5xl font-bold leading-none tracking-tight tabular-nums sm:text-6xl"
          style={{ color: accent }}
        >
          {beat.stat}
        </div>
      )}
      {beat.statNote && (
        <div className="mt-3 font-sans text-[14.5px] font-medium leading-normal text-mauve-shadow-50/70">
          {beat.statNote}
        </div>
      )}
      {beat.lines && beat.lines.length > 0 && (
        <div className="mt-5 flex flex-col gap-2.5">
          {beat.lines.map((line) => (
            <div
              key={line}
              className="flex items-center gap-2.5 font-sans text-[13.5px] font-medium text-mauve-shadow-50/60"
            >
              <span
                className="h-[5px] w-[5px] flex-none rounded-full"
                style={{ backgroundColor: accent }}
              />
              {line}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BeatCopy({ beat }: { beat: IStoryBeat }): React.ReactElement {
  return (
    <div>
      {beat.step && (
        <div className="font-sans text-[13px] font-bold uppercase leading-none tracking-[.1em] text-mint-leaf-400">
          {beat.step}
        </div>
      )}
      <h3 className="mt-4 text-balance font-sans text-4xl font-bold leading-[1.02] tracking-tight text-white sm:text-5xl">
        {beat.title}
      </h3>
      {beat.body && (
        <p className="mt-5 max-w-lg text-pretty font-sans text-lg leading-relaxed text-mauve-shadow-50/70">
          {beat.body}
        </p>
      )}
      {beat.tag && (
        <div
          className="mt-6 flex items-center gap-3 font-sans text-sm font-semibold"
          style={{ color: tagColor(beat) }}
        >
          {beat.icon && (
            <PhosphorIcon
              name={beat.icon}
              className="h-[22px] w-[22px]"
              aria-hidden="true"
            />
          )}
          {beat.tag}
        </div>
      )}
    </div>
  );
}

/** One beat slide; hooks live here so the parent can map over beats safely. */
function BeatSlide({
  beat,
  index,
  count,
  progress,
}: {
  beat: IStoryBeat;
  index: number;
  count: number;
  progress: MotionValue<number>;
}): React.ReactElement {
  const seg = 1 / count;
  const fadeW = FADE * seg;
  const isFirst = index === 0;
  const isLast = index === count - 1;
  // Adjacent beats share each boundary's fade window (beat i fades out while
  // beat i+1 fades in over the same range) so no scroll position is empty.
  const inStart = index * seg - fadeW;
  const inEnd = index * seg;
  const outStart = (index + 1) * seg - fadeW;
  const outEnd = (index + 1) * seg;

  // Function-form transforms keep these on framer's JS update path — the
  // keyframe form gets compiled to WAAPI scroll timelines, which don't track
  // this section-targeted progress correctly.
  const beatOpacity = (p: number): number => {
    if (p < inStart) return isFirst ? 1 : 0;
    if (p < inEnd) return isFirst ? 1 : (p - inStart) / fadeW;
    if (isLast || p <= outStart) return 1;
    if (p < outEnd) return 1 - (p - outStart) / fadeW;
    return 0;
  };
  const opacity = useTransform(progress, beatOpacity);
  const y = useTransform(progress, (p) => {
    if (isFirst && p <= inEnd) return 0;
    const span = isLast ? seg * 0.5 : outEnd - inStart;
    const local = Math.max(0, Math.min(1, (p - inStart) / span));
    return isLast ? 27 * (1 - local) : 27 - 54 * local;
  });
  const scale = useTransform(progress, (p) => 0.986 + 0.014 * beatOpacity(p));
  const pointerEvents = useTransform(progress, (p) =>
    beatOpacity(p) > 0.5 ? ("auto" as const) : ("none" as const)
  );

  return (
    <motion.div
      style={{ opacity, y, scale, pointerEvents, gridArea: "1 / 1" }}
      className="relative grid items-center gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,.95fr)] lg:gap-14"
    >
      <BeatCopy beat={beat} />
      <div className="relative hidden justify-center lg:flex">
        <BeatPanel beat={beat} index={index} />
      </div>
    </motion.div>
  );
}

/**
 * Pinned scroll story ("The nightly ritual"): a tall section whose sticky
 * viewport cross-fades through story beats as the user scrolls, with a
 * progress rail and drifting orbs. Falls back to a static stacked layout on
 * small screens and for reduced motion.
 */
export function PinnedStory({
  heading,
  beats,
}: IPinnedStoryProps): React.ReactElement | null {
  const ref = useRef<HTMLElement>(null);
  const prefersReducedMotion = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  const count = beats.length;
  const railWidth = useTransform(scrollYProgress, (p) => `${(p * 100).toFixed(1)}%`);
  const counterText = useTransform(scrollYProgress, (p) => {
    const idx = Math.max(1, Math.min(count, Math.floor(p * count) + 1));
    return `${String(idx).padStart(2, "0")} / ${String(count).padStart(2, "0")}`;
  });
  const orb1X = useTransform(scrollYProgress, (p) => p * -90);
  const orb1Y = useTransform(scrollYProgress, (p) => p * 60);
  const orb2X = useTransform(scrollYProgress, (p) => p * 80);
  const orb2Y = useTransform(scrollYProgress, (p) => p * -50);

  if (count === 0) return null;

  const showPinned = !prefersReducedMotion;

  return (
    <section
      ref={ref}
      aria-label="Why Tippo exists"
      className={`relative bg-mauve-shadow-950 text-mauve-shadow-50 ${
        showPinned ? (STORY_HEIGHT_CLASS[count] ?? "lg:h-[420vh]") : ""
      }`}
    >
      {/* Pinned variant (desktop, motion allowed) */}
      {showPinned && (
        <div className="sticky top-0 box-border hidden min-h-screen items-center overflow-hidden py-14 lg:flex">
          <motion.div
            aria-hidden="true"
            className="pointer-events-none absolute -top-36 right-[-120px] h-[560px] w-[560px] rounded-full will-change-transform"
            style={{
              x: orb1X,
              y: orb1Y,
              filter: "blur(80px)",
              backgroundImage:
                "radial-gradient(circle at center, rgba(94,161,113,.42) 0%, transparent 62%)",
            }}
          />
          <motion.div
            aria-hidden="true"
            className="pointer-events-none absolute -left-36 bottom-[-200px] h-[520px] w-[520px] rounded-full will-change-transform"
            style={{
              x: orb2X,
              y: orb2Y,
              filter: "blur(80px)",
              backgroundImage:
                "radial-gradient(circle at center, rgba(164,91,131,.4) 0%, transparent 62%)",
            }}
          />

          <div className="relative mx-auto w-full max-w-[1100px] px-7">
            <div className="mb-8 flex items-center gap-3.5">
              <span className="font-sans text-[11px] font-bold uppercase leading-none tracking-[.14em] text-mint-leaf-400">
                {heading}
              </span>
              <span className="relative h-0.5 flex-1 overflow-hidden rounded-full bg-mauve-shadow-50/15">
                <motion.span
                  className="absolute inset-y-0 left-0 bg-mint-leaf-400"
                  style={{ width: railWidth }}
                />
              </span>
              <motion.span className="font-mono text-xs font-semibold text-mauve-shadow-50/60">
                {counterText}
              </motion.span>
            </div>

            <div className="grid">
              {beats.map((beat, i) => (
                <BeatSlide
                  key={beat._key}
                  beat={beat}
                  index={i}
                  count={count}
                  progress={scrollYProgress}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Stacked variant (mobile always; all sizes when reduced motion) */}
      <div
        className={
          showPinned
            ? "px-5 py-20 sm:px-7 lg:hidden"
            : "px-5 py-20 sm:px-7"
        }
      >
        <div className="mx-auto max-w-[1100px]">
          <div className="font-sans text-[11px] font-bold uppercase leading-none tracking-[.14em] text-mint-leaf-400">
            {heading}
          </div>
          <div className="mt-10 flex flex-col gap-16">
            {beats.map((beat, i) => (
              <div key={beat._key} className="grid items-center gap-8 md:grid-cols-2">
                <BeatCopy beat={beat} />
                <div className="flex md:justify-center">
                  <BeatPanel beat={beat} index={i} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
